import { notification } from 'antd';
import * as React from 'react';
import {
  ErrorMessageIcon,
  InfoMessageIcon,
  SuccessMessageIcon,
  WarningMessageIcon,
} from './messageIcons';

export enum MessageStates {
  SUCCESS,
  ERROR,
  INFO,
  WARNING,
  OPEN,
}

export interface MessageData {
  title: string | React.ReactNode;
  description: any;
  type: MessageStates;
  duration?: number;
  button?: any;
}

export const NORMAL_TIMEOUT = 10;
export const ERROR_TIMEOUT = 12;

export type MessageBoxProps = {
  showMessage: boolean;
  message?: MessageData | null;
  onDismiss: () => void;
};

/**
 * Presentational toast system. Apps wire Redux (or other state) and theme tokens.
 * Colors come from each app's theme.css (`--color-status-*`, `--color-primary`, etc.).
 */
export const MessageBox: React.FC<MessageBoxProps> = ({
  showMessage,
  message,
  onDismiss,
}) => {
  const [notificationApi, contextHolder] = notification.useNotification();

  const openMessage = React.useCallback(() => {
    if (!message) return;

    let duration = 4;
    if (!message.duration) {
      if (message.type === MessageStates.ERROR) duration = ERROR_TIMEOUT;
      else duration = NORMAL_TIMEOUT;
    } else {
      duration = message.duration;
    }

    const descriptionTextColor = 'var(--color-text-base, #2e2e2e)';

    const messageDescription =
      message.description == null ? (
        message.description
      ) : typeof message.description === 'string' ? (
        <span
          className="scope-message-toast__description"
          style={{ whiteSpace: 'pre-line', color: descriptionTextColor }}
        >
          {message.description}
        </span>
      ) : (
        <span
          className="scope-message-toast__description"
          style={{ color: descriptionTextColor }}
        >
          {message.description}
        </span>
      );

    let props: any = {
      message: message.title,
      description: messageDescription,
      placement: 'bottomLeft',
      duration,
    };

    if (typeof message.duration === 'number') {
      props = { ...props, duration: message.duration };
    }

    if (message.button) {
      props = { ...props, btn: message.button };
    }

    const msgStyles: React.CSSProperties = {
      borderRadius: '5px',
      padding: '16px 30px 20px 20px',
      paddingLeft: '20px',
      border: 'none',
      color: 'var(--color-text-base, #2e2e2e)',
    };

    const iconStyle: React.CSSProperties = { width: 32, height: 32 };

    switch (message.type) {
      case MessageStates.SUCCESS:
        props = {
          ...props,
          className: 'scope-message-toast scope-message-toast--success',
          icon: (
            <SuccessMessageIcon
              style={{
                ...iconStyle,
                color: 'var(--color-status-success, var(--color-primary, #41aaaf))',
              }}
            />
          ),
          style: {
            ...msgStyles,
            backgroundColor: 'var(--color-status-success-bg, #E9F6F7)',
            border: '1px solid var(--color-status-success, #058181)',
          },
        };
        notificationApi.success(props);
        break;
      case MessageStates.ERROR:
        props = {
          ...props,
          className: 'scope-message-toast scope-message-toast--error',
          icon: (
            <ErrorMessageIcon
              style={{
                ...iconStyle,
                color: 'var(--color-status-danger, #D13131)',
              }}
            />
          ),
          style: {
            ...msgStyles,
            backgroundColor: 'var(--color-status-danger-bg, #FCE7E7)',
            border: '1px solid var(--color-status-danger, #D13131)',
          },
        };
        notificationApi.error(props);
        break;
      case MessageStates.INFO:
        props = {
          ...props,
          className: 'scope-message-toast scope-message-toast--info',
          icon: (
            <InfoMessageIcon
              style={{
                ...iconStyle,
                color: 'var(--color-status-info, var(--color-text-muted, #5a6a6a))',
              }}
            />
          ),
          style: {
            ...msgStyles,
            backgroundColor: 'var(--color-status-info-bg, var(--color-surface, #F3F7F8))',
            border: '1px solid var(--color-status-info, #054581)',
          },
        };
        notificationApi.info(props);
        break;
      case MessageStates.WARNING:
        props = {
          ...props,
          className: 'scope-message-toast scope-message-toast--warning',
          icon: (
            <WarningMessageIcon
              style={{
                ...iconStyle,
                color: 'var(--color-status-warning, #C28A20)',
              }}
            />
          ),
          style: {
            ...msgStyles,
            backgroundColor: 'var(--color-status-warning-bg, #FFF5E2)',
            border: '1px solid var(--color-status-warning, #C28A20)',
          },
        };
        notificationApi.warning(props);
        break;
      case MessageStates.OPEN:
        props = {
          ...props,
          className: 'scope-message-toast scope-message-toast--open',
          style: {
            ...msgStyles,
            backgroundColor: 'var(--color-surface-raised, #ffffff)',
            border: '1px solid var(--color-border, #dbeae3)',
          },
        };
        notificationApi.open(props);
        break;
    }

    onDismiss();
  }, [message, notificationApi, onDismiss]);

  React.useEffect(() => {
    if (showMessage) openMessage();
    // Intentionally only showMessage: same as generate-tax MessageBox
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMessage]);

  return <div>{contextHolder}</div>;
};
